import AddCircleIcon from "@mui/icons-material/AddCircle";
import HomeIcon from "@mui/icons-material/Home";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Container,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BottomSheetHeader, PageTitleBar } from "common/components";
import DetailViewLoading from "common/components/DetailViewLoading";
import { useTranslation } from "common/locales";
import { FieldsList } from "features/farmOverview";
import { GrassWedgeDialog } from "features/grassWedge";
import {
  getAllFieldsForAnimalGroupIdFromArray,
  getAnimalGroupColorFromArray,
  getAvailableDryMatterForAnimalGroupIdFromArray,
  getDemandFromArray,
  getFieldGeomsForAnimalGroupFromArray,
  getTotalAnimalCountFromArray,
  getTotalLivestockUnitsFromArray,
  StockClassFeedAllocation,
  useAnimalGroup,
  useCurrentFarm,
  useCurrentFarmAnimalGroups,
  useFeedAllocationsByIds,
  useFields,
  useStockClassesByIds,
} from "model";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAppStore, useUIStore } from "stores";
import { analytics, AnalyticsEvents } from "../../common/analytics";
import AnimalGroupMenu from "./AnimalGroupMenu";
import AssignFieldDialog from "./AssignFieldDialog";
import SetCurrentFieldDialog from "./SetCurrentFieldDialog";

import { feedBudgetPlannerEnabledForUserAndAnimalGroup } from "common/featureToggle";
import { FeedAllocationDialog } from "features/feedAllocation";
import {
  RotationTimelineButton,
  RotationTimelineDialog,
} from "./RotationTimeline";

const BELLFLOWER_COLOR = "#D3C9E7";

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  secondaryValue?: string;
  warning?: boolean;
  infoTooltip?: string;
}

function MetricItem({
  label,
  value,
  unit,
  secondaryValue,
  warning,
  infoTooltip,
}: MetricItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        borderBottom: "1px solid #F0F0F0",
        "&:last-child": {
          borderBottom: "none",
        },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
        {infoTooltip && (
          <Tooltip title={infoTooltip} arrow placement="top">
            <InfoOutlinedIcon
              sx={{
                fontSize: 14,
                color: "text.secondary",
                cursor: "help",
                opacity: 0.6,
                "&:hover": { opacity: 1 },
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: warning ? "#DC2626" : "#1a1a2e",
            fontStyle: warning ? "italic" : "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
        {unit && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.7rem", flexShrink: 0 }}
          >
            {unit}
          </Typography>
        )}
        {secondaryValue && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.65rem", flexShrink: 0 }}
          >
            ({secondaryValue})
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface WarningItemProps {
  label: string;
  value?: string;
  action?: React.ReactNode;
}

function WarningItem({ label, value, action }: WarningItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        mx: -1,
        borderBottom: "1px solid #F7E880",
        backgroundColor: "#FDFBEB",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "#A48137", display: "block", fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      {value && (
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#A48137" }}>
          {value}
        </Typography>
      )}
      {action}
    </Box>
  );
}

interface MetricsCardProps {
  children: React.ReactNode;
}

function MetricsCard({ children }: MetricsCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${BELLFLOWER_COLOR}`,
      }}
    >
      {children}
    </Box>
  );
}

interface StockClassRowProps {
  name: string;
  count: number;
}

function StockClassRow({ name, count }: StockClassRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.5,
        borderBottom: "1px dotted #E0E0E0",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, color: "#1a1a2e", flexShrink: 0 }}
      >
        {count}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontSize: "0.7rem",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          minWidth: 0,
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

export default function AnimalGroupDetail() {
  const [assignFieldDialogOpen, setAssignFieldDialogOpen] =
    useState<boolean>(false);
  const [setCurrentFieldDialogOpen, setSetCurrentFieldDialogOpen] =
    useState<boolean>(false);
  const { animalGroupId } = useParams<{ animalGroupId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open assign field dialog when coming from onboarding
  useEffect(() => {
    if (searchParams.get("openAssignField") === "true") {
      setAssignFieldDialogOpen(true);
      // Clear the param so refreshing doesn't reopen the dialog
      searchParams.delete("openAssignField");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { data: currentFarm } = useCurrentFarm();

  // Use proper React Query hook for animal group data (fetches if not in cache)
  const { data: animalGroup } = useAnimalGroup(animalGroupId);

  // Get stock classes for this animal group
  const { data: stockClasses = [] } = useStockClassesByIds(
    animalGroup?.stockClasses
  );

  // Compute animal count and livestock units from stock classes
  const animalCount = useMemo(
    () => getTotalAnimalCountFromArray(stockClasses),
    [stockClasses]
  );
  const livestockUnits = useMemo(
    () => getTotalLivestockUnitsFromArray(stockClasses),
    [stockClasses]
  );

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  const user = useAppStore((state) => state.user);
  const feedBudgetPlannerEnabled =
    feedBudgetPlannerEnabledForUserAndAnimalGroup(user, animalGroup);

  const grassWedgeMatch = useMatch("/animal-group/:animalGroupId/grass-wedge");
  const feedAllocationMatch = useMatch(
    "/animal-group/:animalGroupId/feed-allocation"
  );
  const rotationTimelineMatch = useMatch(
    "/animal-group/:animalGroupId/rotations"
  );
  const { t } = useTranslation();

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fields = useMemo(
    () => getAllFieldsForAnimalGroupIdFromArray(allFields, animalGroupId),
    [allFields, animalGroupId]
  );

  // Get all animal groups to compute color by index
  const { data: allAnimalGroups = [] } = useCurrentFarmAnimalGroups();

  // Get animal group color for visual accent
  const animalGroupColor = useMemo(
    () =>
      animalGroup?.color ||
      getAnimalGroupColorFromArray(allAnimalGroups, animalGroupId),
    [allAnimalGroups, animalGroupId, animalGroup?.color]
  );

  // Collect all feed allocation IDs from stock classes
  const feedAllocationIds = useMemo(
    () => stockClasses.flatMap((sc) => sc.feedAllocations || []),
    [stockClasses]
  );

  // Fetch feed allocations
  const { data: feedAllocations = [] } = useFeedAllocationsByIds(
    feedAllocationIds,
    feedAllocationIds.length > 0
  );

  const dryMatterAvailable = useMemo(
    () => getAvailableDryMatterForAnimalGroupIdFromArray(fields, animalGroup),
    [fields, animalGroup]
  );

  // Compute demand from stock classes
  const demand = useMemo(
    () => getDemandFromArray(stockClasses),
    [stockClasses]
  );

  // Compute demand from feed allocations (more accurate when feed budget planner is enabled)
  const feedAllocationDemand = useMemo(() => {
    let demandTotal = 0;
    const now = new Date().valueOf();

    for (const sc of stockClasses) {
      const feedAllocationsForStockClass = feedAllocations.filter(
        (fa: StockClassFeedAllocation) =>
          sc.feedAllocations?.includes(fa.guid) &&
          new Date(fa.dateStart).valueOf() <= now
      );

      feedAllocationsForStockClass.sort(
        (a: StockClassFeedAllocation, b: StockClassFeedAllocation) =>
          new Date(b.dateStart).valueOf() - new Date(a.dateStart).valueOf()
      );

      const currentFeedAllocation = feedAllocationsForStockClass.at(0);
      if (currentFeedAllocation) {
        demandTotal +=
          sc.animalCount *
          sc.averageAnimalWeightKg *
          currentFeedAllocation.allocatedBodyWeightFraction;
      }
    }
    return demandTotal;
  }, [stockClasses, feedAllocations]);
  const totalAreaForGroup = fields.reduce((acc, f) => acc + f.area, 0);
  let demandPerHaAndDay = demand
    ? Math.round((demand / totalAreaForGroup) * 10000)
    : 0;

  if (feedBudgetPlannerEnabled) {
    demandPerHaAndDay = feedAllocationDemand
      ? Math.round((feedAllocationDemand / totalAreaForGroup) * 10000)
      : 0;
  }

  const showGrassWedgeButton = true; // always allow to open grasswedge

  useZoomToFieldGeoms(animalGroupId);

  if (!animalGroup) return <DetailViewLoading size={2} />;

  const { name } = animalGroup;

  // Calculate metrics
  const fieldCount = fields.length;
  const totalAreaHa = totalAreaForGroup / 10000; // Convert m² to Ha
  const totalAreaRounded = Math.round(totalAreaHa * 10) / 10;

  // Find current field (where animals are grazing now)
  const currentField = fields.find((f) => f.animalGroupNow === animalGroupId);

  const openMoveOutEventDialog = useUIStore(
    (state) => state.openMoveOutEventDialog
  );
  const handleMoveToShed = () => {
    if (!currentField) return;
    openMoveOutEventDialog({
      open: true,
      currentFieldWithAnimals: currentField,
      moveToShed: true,
    });
  };

  // Convert dry matter to tonnes
  const dryMatterTonnes = (dryMatterAvailable / 1000).toFixed(1);

  // Calculate total daily demand from stock classes (in kg)
  const totalDailyDemand = stockClasses
    ? stockClasses.reduce(
        (sum, sc) => sum + sc.consumptionKgPerDay * sc.animalCount,
        0
      )
    : 0;

  // Calculate days of forage available (grazing days ahead)
  const grazingDaysAhead =
    totalDailyDemand > 0
      ? Math.round(dryMatterAvailable / totalDailyDemand)
      : 0;

  // Calculate stocking rate (LU/Ha)
  const stockingRate =
    totalAreaHa > 0 ? (livestockUnits / totalAreaHa).toFixed(1) : "-";

  return (
    <>
      {isMobile ? (
        <BottomSheetHeader
          titlePrefix={`${t("animalGroup.prefix")}:`}
          title={name}
          onBack={handleBackToFarm}
          rightAction={<AnimalGroupMenu {...animalGroup} />}
        />
      ) : (
        <PageTitleBar
          titlePrefix={`${t("animalGroup.prefix")}:`}
          title={name}
          onBack={handleBackToFarm}
        >
          <AnimalGroupMenu {...animalGroup} />
        </PageTitleBar>
      )}

      {/* Animal Group Color Accent Strip */}
      {animalGroupColor && (
        <Box
          sx={{
            height: 4,
            backgroundColor: animalGroupColor,
            width: "100%",
            mb: 2,
          }}
        />
      )}

      <Container
        maxWidth="sm"
        sx={{
          pb: isMobile ? 10 : 2,
          pt: animalGroupColor ? 0 : 2,
        }}
      >
        {/* Current Field Warning Banner */}
        {fieldCount > 0 && !currentField && (
          <Box
            sx={{
              backgroundColor: "#FDFBEB",
              border: "1px solid #F7E880",
              borderRadius: 2,
              px: 1.5,
              py: 1,
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#A48137", fontSize: "0.8rem" }}
            >
              {t("animalGroup.currentFieldNotSet")}
            </Typography>
            <Button
              size="small"
              onClick={() => setSetCurrentFieldDialogOpen(true)}
              sx={{
                color: "#A48137",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                padding: "4px 12px",
                minWidth: "auto",
                backgroundColor: "rgba(164, 129, 55, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(164, 129, 55, 0.2)",
                },
              }}
            >
              {t("animalGroup.setCurrentField")}
            </Button>
          </Box>
        )}

        {/* Move to Shed Button */}
        {currentField && (
          <Box sx={{ mb: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={handleMoveToShed}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderColor: "#7D5DA3",
                color: "#7D5DA3",
                "&:hover": {
                  borderColor: "#5E3F87",
                  backgroundColor: "rgba(125, 93, 163, 0.08)",
                },
              }}
            >
              {t("animalGroup.moveToShed")}
            </Button>
          </Box>
        )}

        {/* Animal Group Metrics */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            {/* First card: Animals (with LU), Assigned Area, Stocking Rate */}
            <MetricsCard>
              <MetricItem
                label={t("animalGroup.animalCount")}
                value={animalCount || 0}
                secondaryValue={`${livestockUnits.toFixed(1)} LU`}
                infoTooltip={t("animalGroup.metrics.animalCount.info")}
              />
              {fieldCount > 0 ? (
                <MetricItem
                  label={t("animalGroup.metrics.assignedArea")}
                  value={totalAreaRounded}
                  unit="Ha"
                  infoTooltip={t("animalGroup.metrics.assignedArea.info")}
                />
              ) : (
                <WarningItem
                  label={t("animalGroup.metrics.assignedArea")}
                  value={t("animalGroup.metrics.none")}
                />
              )}
              <MetricItem
                label={t("animalGroup.metrics.stockingRate")}
                value={stockingRate}
                unit={stockingRate !== "-" ? "LU/Ha" : undefined}
                infoTooltip={t("animalGroup.metrics.stockingRate.info")}
              />
            </MetricsCard>

            {/* Second card: Forage Available, Daily Feed Demand, Grazing Days */}
            <MetricsCard>
              <MetricItem
                label={t("animalGroup.metrics.forageAvailable")}
                value={dryMatterTonnes}
                unit="t DM"
                infoTooltip={t("animalGroup.metrics.forageAvailable.info")}
              />
              <MetricItem
                label={t("animalGroup.metrics.dailyFeedDemand")}
                value={
                  totalDailyDemand > 0 ? Math.round(totalDailyDemand) : "-"
                }
                unit={totalDailyDemand > 0 ? "kgDM/day" : undefined}
                infoTooltip={t("animalGroup.metrics.dailyFeedDemand.info")}
              />
              <MetricItem
                label={t("animalGroup.metrics.grazingDaysAhead")}
                value={grazingDaysAhead > 0 ? grazingDaysAhead : "-"}
                unit={grazingDaysAhead > 0 ? "days" : undefined}
                infoTooltip={t("animalGroup.metrics.grazingDaysAhead.info")}
              />
            </MetricsCard>
          </Box>

          {/* Stock Classes */}
          {stockClasses && stockClasses.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <MetricsCard>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    fontSize: "0.7rem",
                    pt: 0.5,
                    pb: 0.25,
                  }}
                >
                  {t("animalGroup.metrics.stockClasses")}
                </Typography>
                {stockClasses.map((sc) => (
                  <StockClassRow
                    key={sc.guid}
                    name={sc.name}
                    count={sc.animalCount}
                  />
                ))}
              </MetricsCard>
            </Box>
          )}
        </Box>

        {/* Grass Wedge Preview Card */}
        <Box sx={{ mb: 2 }}>
          <GrassWedgePreviewCard
            animalGroupId={animalGroupId}
            disabled={!showGrassWedgeButton}
          />
          {showGrassWedgeButton && (
            <GrassWedgeDialog
              open={Boolean(grassWedgeMatch)}
              close={() => navigate(`/animal-group/${animalGroupId}`)}
              animalGroup={animalGroup}
            />
          )}
          {feedBudgetPlannerEnabled && (
            <Box sx={{ mt: 1 }}>
              <FeedAllocationButton animalGroupId={animalGroupId} />
            </Box>
          )}
          {feedBudgetPlannerEnabled && (
            <FeedAllocationDialog
              open={Boolean(feedAllocationMatch)}
              close={() => navigate(`/animal-group/${animalGroupId}`)}
              animalGroup={animalGroup}
            />
          )}
          <Box sx={{ mt: 1 }}>
            <RotationTimelineButton animalGroupId={animalGroupId} />
          </Box>
          <RotationTimelineDialog
            open={Boolean(rotationTimelineMatch)}
            close={() => navigate(`/animal-group/${animalGroupId}`)}
            animalGroupId={animalGroupId}
            animalGroupName={animalGroup?.name ?? ""}
            animalGroup={animalGroup}
            stockClasses={stockClasses}
          />
        </Box>

        {/* Assigned Fields Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {t("animalGroup.metrics.assignedFields")}
            </Typography>
            <Button
              startIcon={<AddCircleIcon sx={{ fontSize: "16px !important" }} />}
              size="small"
              onClick={() => {
                analytics.track(
                  AnalyticsEvents.ANIMAL_GROUP_ASSIGN_FIELDS_CLICKED
                );
                setAssignFieldDialogOpen(true);
              }}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "4px 8px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(56, 142, 60, 0.08)",
                },
              }}
            >
              {t("animalGroup.assignField")}
            </Button>
          </Box>
          <AssignFieldDialog
            open={assignFieldDialogOpen}
            close={() => setAssignFieldDialogOpen(false)}
            animalGroupId={animalGroupId}
          />
          <SetCurrentFieldDialog
            open={setCurrentFieldDialogOpen}
            close={() => setSetCurrentFieldDialogOpen(false)}
            animalGroupId={animalGroupId}
          />
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              border: `1px solid ${BELLFLOWER_COLOR}`,
              overflow: "hidden",
            }}
          >
            <FieldsList fieldIds={fields.map((field) => field.guid)} />
          </Box>
        </Box>
      </Container>
    </>
  );
}

function useZoomToFieldGeoms(animalGroupId: string) {
  const setZoomToGeom = useUIStore((state) => state.setZoomToGeom);
  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fieldGeoms = useMemo(
    () => getFieldGeomsForAnimalGroupFromArray(allFields, animalGroupId),
    [allFields, animalGroupId]
  );
  // Track which animal group we've zoomed to, so we only zoom once per animal group
  const zoomedToAnimalGroupRef = useRef<string | null>(null);

  useEffect(() => {
    // Only zoom if we haven't already zoomed to this animal group
    if (zoomedToAnimalGroupRef.current === animalGroupId) {
      return;
    }

    if (fieldGeoms.features.length > 0) {
      zoomedToAnimalGroupRef.current = animalGroupId;
      setZoomToGeom(fieldGeoms);
    }
  }, [animalGroupId, fieldGeoms, setZoomToGeom]);
}

function GrassWedgePreviewCard({
  animalGroupId,
  disabled,
}: GrassWedgeCardProps) {
  const { t } = useTranslation();

  // Bar heights and colors for the preview chart (descending order - tallest first)
  // Colors go from dark (tall grass) to light (short grass)
  const barHeights = [85, 80, 75, 70, 65, 60, 45];
  const barColors = [
    "#2E7D32",
    "#388E3C",
    "#43A047",
    "#4CAF50",
    "#66BB6A",
    "#81C784",
    "#A5D6A7",
  ];

  const cardContent = (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
            },
      }}
    >
      {/* Chart Preview Area */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          position: "relative",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 80 }}
        >
          {/* Y-axis placeholder lines */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
              pr: 1,
            }}
          >
            {[null, null, null].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 20,
                  height: 2,
                  backgroundColor: "#E0E0E0",
                  borderRadius: 1,
                }}
              />
            ))}
          </Box>

          {/* Bars */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 0.75,
              flex: 1,
              height: "100%",
              position: "relative",
            }}
          >
            {barHeights.map((height, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: `${height}%`,
                  backgroundColor: barColors[index],
                  borderRadius: "4px 4px 0 0",
                  minWidth: 16,
                }}
              />
            ))}

            {/* Trend line overlay */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                pointerEvents: "none",
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ overflow: "visible" }}
              >
                <line
                  x1="0"
                  y1="20"
                  x2="100"
                  y2="60"
                  stroke="#7D5DA3"
                  strokeWidth="4"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer with label */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid #F0F0F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1C4321",
            fontSize: "0.875rem",
          }}
        >
          {t("grassWedge.open")}
        </Typography>
      </Box>
    </Box>
  );

  if (disabled) {
    return (
      <Tooltip title={t("animalGroup.grassWedgeButton.tooltip")} arrow>
        <Box>{cardContent}</Box>
      </Tooltip>
    );
  }

  return (
    <Link
      to={`/animal-group/${animalGroupId}/grass-wedge`}
      style={{ textDecoration: "none" }}
    >
      {cardContent}
    </Link>
  );
}

type GrassWedgeCardProps = {
  animalGroupId: string;
  disabled?: boolean;
};

const StyledButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1),
}));

function FeedAllocationButton({ animalGroupId }: { animalGroupId: string }) {
  const { t } = useTranslation();
  return (
    <StyledButton
      variant="contained"
      color="primary"
      component={Link}
      onClick={() => {
        analytics.track(AnalyticsEvents.FEED_BUDGET_VIEW_OPENED);
      }}
      to={`/animal-group/${animalGroupId}/feed-allocation`}
    >
      {t("animalGroup.viewFeedAllocation")}
    </StyledButton>
  );
}
